import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const location = useLocation();
  const [pricingPlan, setPricingPlan] = useState('annual');

  useEffect(() => {
    // Initialize WOW.js for animations
    if (window.WOW) {
      new window.WOW().init();
    }

    // Handle scrolling to section if coming from another page
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // Small delay to ensure page is fully rendered
    }
  }, [location]);

  return (
    <div className="landing-page modern-dark">
      {/* Hero Section with Modern Dark Theme */}
      <section className="ud-hero modern-hero" id="home">
        <div className="hero-background-gradient"></div>
        <div className="hero-particles"></div>
        <div className="hero-animated-grid"></div>
        <div className="hero-neural-network">
          <div className="neural-dot" style={{top: '20%', left: '15%'}}></div>
          <div className="neural-dot" style={{top: '40%', left: '25%'}}></div>
          <div className="neural-dot" style={{top: '60%', left: '35%'}}></div>
          <div className="neural-dot" style={{top: '30%', left: '70%'}}></div>
          <div className="neural-dot" style={{top: '50%', left: '80%'}}></div>
          <div className="neural-dot" style={{top: '70%', left: '90%'}}></div>
          <div className="neural-dot" style={{top: '80%', left: '20%'}}></div>
          <div className="neural-dot" style={{top: '15%', left: '85%'}}></div>
        </div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-hero-content wow fadeInUp" data-wow-delay=".2s">
                <h1 className="ud-hero-title gradient-text">
                  NovaMatch – Intelligent Hiring Starts Here
                </h1>
                <p className="ud-hero-desc light-text">
                  AI-powered resume and job description matching for faster, smarter recruitment decisions.
                </p>
                <p className="ud-hero-desc light-text">
                  NovaMatch is an AI-powered hiring platform that matches resumes to job requirements, assesses technical fit, experience, and commitment, generates HR verdicts and screening questions, and ranks best-fit candidates — enabling HR teams, hiring managers, and business leaders to make confident, data-driven hiring decisions in one unified system.
                </p>
                <ul className="ud-hero-buttons">
                  <li>
                    <Link to="/register" className="ud-main-btn ud-gradient-btn">
                      Get Started
                    </Link>
                  </li>
                  <li>
                    <Link to="/login" className="ud-main-btn ud-glass-btn">
                      Login <i className="lni lni-arrow-right"></i>
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="ud-hero-image wow fadeInUp" data-wow-delay=".25s">
                <div className="hero-image-wrapper">
                  <img src="/assets/images/hero/header-image.png" alt="hero-image" className="floating-animation" />
                  <div className="gradient-orb orb-1"></div>
                  <div className="gradient-orb orb-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Glass Morphism */}
      <section id="features" className="ud-features modern-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-section-title text-center mx-auto" style={{ maxWidth: '900px' }}>
                <span className="section-badge">Features</span>
                <h2 className="gradient-text">Powerful Features Built for Modern Hiring Teams</h2>
                <p className="light-text" style={{ textAlign: 'center', margin: '0 auto' }}>NovaMatch provides intelligent tools to streamline hiring workflows and enable smarter talent assessment and decision-making.</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-feature glass-card wow fadeInUp" data-wow-delay=".1s">
                <div className="ud-feature-icon gradient-icon">
                  <i className="lni lni-files"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Resume vs Job Matching</h3>
                  <p className="ud-feature-desc">
                    Compare resumes with job descriptions to determine overall fit and role alignment.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-feature glass-card wow fadeInUp" data-wow-delay=".15s">
                <div className="ud-feature-icon gradient-icon">
                  <i className="lni lni-stats-up"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Intelligent Matching Score</h3>
                  <p className="ud-feature-desc">
                    Get a fit score based on technical skills, experience, and role relevance.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-feature glass-card wow fadeInUp" data-wow-delay=".2s">
                <div className="ud-feature-icon gradient-icon">
                  <i className="lni lni-checkmark-circle"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Skill, Keyword & Experience Analysis</h3>
                  <p className="ud-feature-desc">
                    Identify matched, missing, and partial skills and keywords, and assess experience depth.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-feature glass-card wow fadeInUp" data-wow-delay=".25s">
                <div className="ud-feature-icon gradient-icon">
                  <i className="lni lni-timer"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">AI-Driven Hiring Insights</h3>
                  <p className="ud-feature-desc">
                    Generate HR verdicts, commitment indicators, missing skill gaps, and screening questions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="ud-pricing modern-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-section-title mx-auto text-center">
                <span className="section-badge">How It Works</span>
                <h2 className="gradient-text">How NovaMatch Works</h2>
                <p className="light-text">Four simple steps to intelligent talent assessment and confident hiring decisions.</p>
              </div>
            </div>
          </div>

          <div className="row g-0 align-items-stretch justify-content-center">
            <div className="col-lg-3 col-md-6 col-sm-10 d-flex">
              <div className="ud-single-pricing first-item wow fadeInUp" data-wow-delay=".15s">
                <div className="ud-pricing-header">
                  <h3>STEP 1</h3>
                  <div className="ud-feature-icon" style={{ margin: '20px auto' }}>
                    <i className="lni lni-upload"></i>
                  </div>
                </div>
                <div className="ud-pricing-body">
                  <h4 style={{ marginBottom: '15px' }}>Upload Resumes</h4>
                  <p>Upload candidate resumes for intelligent parsing and analysis.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-10 d-flex">
              <div className="ud-single-pricing wow fadeInUp" data-wow-delay=".1s">
                <div className="ud-pricing-header">
                  <h3>STEP 2</h3>
                  <div className="ud-feature-icon" style={{ margin: '20px auto' }}>
                    <i className="lni lni-write"></i>
                  </div>
                </div>
                <div className="ud-pricing-body">
                  <h4 style={{ marginBottom: '15px' }}>Add Job Description</h4>
                  <p>Provide the job description to define role requirements and skills.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-10 d-flex">
              <div className="ud-single-pricing wow fadeInUp" data-wow-delay=".15s">
                <div className="ud-pricing-header">
                  <h3>STEP 3</h3>
                  <div className="ud-feature-icon" style={{ margin: '20px auto' }}>
                    <i className="lni lni-cog"></i>
                  </div>
                </div>
                <div className="ud-pricing-body">
                  <h4 style={{ marginBottom: '15px' }}>AI Assessment & HR Insights</h4>
                  <p>Assess technical fit, experience depth, commitment, and missing skills. Generate HR verdicts and role-specific screening questions.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-10 d-flex">
              <div className="ud-single-pricing last-item wow fadeInUp" data-wow-delay=".2s">
                <div className="ud-pricing-header">
                  <h3>STEP 4</h3>
                  <div className="ud-feature-icon" style={{ margin: '20px auto' }}>
                    <i className="lni lni-graph"></i>
                  </div>
                </div>
                <div className="ud-pricing-body">
                  <h4 style={{ marginBottom: '15px' }}>Ranking & Hiring Decisions</h4>
                  <p>Rank best-fit candidates and make confident, data-driven hiring decisions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section id="faq" className="ud-faq modern-section">
        <div className="shape">
          <img src="/assets/images/faq/shape.svg" alt="shape" style={{opacity: 0.1}} />
        </div>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-section-title text-center mx-auto">
                <span className="section-badge">Why NovaMatch</span>
                <h2 className="gradient-text">Why Choose NovaMatch?</h2>
                <p className="light-text">Discover how NovaMatch enables intelligent talent assessment and confident hiring decisions.</p>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".1s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-timer"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Intelligent Talent Assessment</h3>
                  <p className="ud-feature-desc">
                    Assess technical fit, experience, and commitment.
                  </p>
                </div>
              </div>
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".15s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-target"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">HR Verdicts & Screening Questions</h3>
                  <p className="ud-feature-desc">
                    Get structured hiring insights.
                  </p>
                </div>
              </div>
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".2s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-wallet"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">No Senior Resource Dependency</h3>
                  <p className="ud-feature-desc">
                    Eliminate manual evaluation by senior staff.
                  </p>
                </div>
              </div>
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".25s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-graph"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Improve Hiring Accuracy</h3>
                  <p className="ud-feature-desc">
                    Make confident, data-driven decisions.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".1s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-rocket"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Smarter Shortlisting</h3>
                  <p className="ud-feature-desc">
                    Rank candidates by best fit.
                  </p>
                </div>
              </div>
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".15s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-revenue"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Reduce Hiring Risk & Cost</h3>
                  <p className="ud-feature-desc">
                    Avoid mis-hires and hiring waste.
                  </p>
                </div>
              </div>
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".2s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-checkmark-circle"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Focus on the Right Candidates</h3>
                  <p className="ud-feature-desc">
                    Engage with candidates who truly match.
                  </p>
                </div>
              </div>
              <div className="ud-single-feature wow fadeInUp" data-wow-delay=".25s" style={{ padding: '30px', marginBottom: '30px' }}>
                <div className="ud-feature-icon">
                  <i className="lni lni-layers"></i>
                </div>
                <div className="ud-feature-content">
                  <h3 className="ud-feature-title">Unified Hiring Intelligence</h3>
                  <p className="ud-feature-desc">
                    All hiring insights in one platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="testimonials" className="ud-testimonials modern-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-section-title mx-auto text-center">
                <span className="section-badge">Secure & Reliable</span>
                <h2 className="gradient-text">Secure & Reliable Platform</h2>
                <p className="light-text">Your recruitment data stays secure, private, and always available with NovaMatch.</p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-3 col-md-6">
              <div className="ud-single-testimonial glass-card wow fadeInUp" data-wow-delay=".1s" style={{ padding: '25px 20px' }}>
                <div className="ud-feature-icon gradient-icon" style={{ margin: '15px auto' }}>
                  <i className="lni lni-lock" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <div className="ud-testimonial-content">
                  <h4 className="ud-feature-title" style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Secure Authentication</h4>
                  <p className="ud-feature-desc" style={{ fontSize: '0.9rem' }}>
                    Enterprise-grade security with multi-factor authentication to protect user access and sensitive recruitment data.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="ud-single-testimonial glass-card wow fadeInUp" data-wow-delay=".15s" style={{ padding: '25px 20px' }}>
                <div className="ud-feature-icon gradient-icon" style={{ margin: '15px auto' }}>
                  <i className="lni lni-shield" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <div className="ud-testimonial-content">
                  <h4 className="ud-feature-title" style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Data Privacy Protection</h4>
                  <p className="ud-feature-desc" style={{ fontSize: '0.9rem' }}>
                    Candidate data is encrypted at rest and in transit, and securely stored following industry best practices and compliance standards.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="ud-single-testimonial glass-card wow fadeInUp" data-wow-delay=".2s" style={{ padding: '25px 20px' }}>
                <div className="ud-feature-icon gradient-icon" style={{ margin: '15px auto' }}>
                  <i className="lni lni-dashboard" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <div className="ud-testimonial-content">
                  <h4 className="ud-feature-title" style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Reliable Performance</h4>
                  <p className="ud-feature-desc" style={{ fontSize: '0.9rem' }}>
                    High-performance, scalable platform designed to handle large resume volumes with consistent uptime and reliable system availability.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="ud-single-testimonial glass-card wow fadeInUp" data-wow-delay=".25s" style={{ padding: '25px 20px' }}>
                <div className="ud-feature-icon gradient-icon" style={{ margin: '15px auto' }}>
                  <i className="lni lni-files" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <div className="ud-testimonial-content">
                  <h4 className="ud-feature-title" style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Scan History & Assessment Records</h4>
                  <p className="ud-feature-desc" style={{ fontSize: '0.9rem' }}>
                    View and manage a complete history of all previously scanned resumes, assessments, and results for easy reference and comparison.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-12">
              <div className="text-center" style={{ marginTop: '40px' }}>
                <p className="light-text" style={{ fontSize: '0.95rem', opacity: 0.8 }}>
                  <strong>Compliance & Trust:</strong> GDPR-ready • SOC 2–aligned • Built with enterprise security best practices
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is It For Section */}
      <section id="who-for" className="ud-team modern-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-section-title mx-auto text-center">
                <span className="section-badge">Who Can Use</span>
                <h2 className="gradient-text">Who Is It For?</h2>
                <p className="light-text">Built for modern hiring teams of all sizes.</p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-team wow fadeInUp" data-wow-delay=".1s">
                <div className="ud-team-image-wrapper">
                  <div className="ud-team-image" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '170px' }}>
                    <i className="lni lni-users" style={{ fontSize: '45px', color: 'white' }}></i>
                  </div>
                </div>
                <div className="ud-team-info">
                  <h5>Recruiters</h5>
                  <h6>Assess resumes, generate HR verdicts, and shortlist smarter.</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-team wow fadeInUp" data-wow-delay=".15s">
                <div className="ud-team-image-wrapper">
                  <div className="ud-team-image" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '170px' }}>
                    <i className="lni lni-briefcase" style={{ fontSize: '45px', color: 'white' }}></i>
                  </div>
                </div>
                <div className="ud-team-info">
                  <h5>HR Managers</h5>
                  <h6>AI-powered talent assessment and hiring insights.</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-team wow fadeInUp" data-wow-delay=".2s">
                <div className="ud-team-image-wrapper">
                  <div className="ud-team-image" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '170px' }}>
                    <i className="lni lni-network" style={{ fontSize: '45px', color: 'white' }}></i>
                  </div>
                </div>
                <div className="ud-team-info">
                  <h5>Staffing Agencies</h5>
                  <h6>Intelligent matching for multiple client roles.</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-sm-6">
              <div className="ud-single-team wow fadeInUp" data-wow-delay=".25s">
                <div className="ud-team-image-wrapper">
                  <div className="ud-team-image" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '170px' }}>
                    <i className="lni lni-rocket" style={{ fontSize: '45px', color: 'white' }}></i>
                  </div>
                </div>
                <div className="ud-team-info">
                  <h5>Startups & SMBs</h5>
                  <h6>Cost-effective hiring without senior resources.</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Section */}
      <section id="subscription" className="ud-pricing modern-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ud-section-title mx-auto text-center">
                <span className="section-badge">Pricing Plans</span>
                <h2 className="gradient-text">Upgrade to Premium</h2>
                <p className="light-text">Choose the plan that best fits your hiring needs</p>
              </div>
            </div>
          </div>

          {/* Pricing Toggle */}
          <div className="row">
            <div className="col-lg-12">
              <div className="pricing-toggle text-center mb-5">
                <button 
                  className={`pricing-toggle-btn ${pricingPlan === 'annual' ? 'active' : ''}`}
                  onClick={() => setPricingPlan('annual')}
                >
                  ANNUAL <span className="badge-green">BEST VALUE</span>
                </button>
                <button 
                  className={`pricing-toggle-btn ${pricingPlan === 'monthly' ? 'active' : ''}`}
                  onClick={() => setPricingPlan('monthly')}
                >
                  MONTHLY
                </button>
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="row g-4 justify-content-center">
            {/* Basic Plan */}
            <div className="col-lg-4 col-md-6">
              <div className="pricing-card glass-card wow fadeInUp" data-wow-delay=".1s">
                <div className="pricing-header">
                  <h3 className="plan-name">Basic</h3>
                  <p className="plan-subtitle">Best for individuals / starters</p>
                  <div className="price-wrapper">
                    <span className="currency">$</span>
                    <span className="price">{pricingPlan === 'annual' ? '8.33' : '10'}</span>
                    <span className="period">/ month</span>
                  </div>
                  {pricingPlan === 'annual' ? (
                    <>
                      <p className="billing-info">$100 billed annually</p>
                      <span className="save-badge">SAVE 20%</span>
                    </>
                  ) : (
                    <p className="billing-info">Billed monthly</p>
                  )}
                </div>
                <div className="pricing-features">
                  <ul>
                    <li><i className="lni lni-checkmark-circle"></i> 10 resume analyses per day</li>
                    <li><i className="lni lni-checkmark-circle"></i> Limited daily usage</li>
                    <li><i className="lni lni-checkmark-circle"></i> Basic AI matching</li>
                    <li><i className="lni lni-checkmark-circle"></i> Standard support</li>
                    <li><i className="lni lni-checkmark-circle"></i> Export results as PDF</li>
                  </ul>
                </div>
                <Link to="/login" className="pricing-btn">Upgrade</Link>
              </div>
            </div>

            {/* Plus Plan */}
            <div className="col-lg-4 col-md-6">
              <div className="pricing-card glass-card wow fadeInUp" data-wow-delay=".15s">
                <div className="pricing-header">
                  <h3 className="plan-name">Plus</h3>
                  <p className="plan-subtitle">Best for regular users</p>
                  <div className="price-wrapper">
                    <span className="currency">$</span>
                    <span className="price">{pricingPlan === 'annual' ? '12.50' : '15'}</span>
                    <span className="period">/ month</span>
                  </div>
                  {pricingPlan === 'annual' ? (
                    <>
                      <p className="billing-info">$150 billed annually</p>
                      <span className="save-badge">SAVE 30%</span>
                    </>
                  ) : (
                    <p className="billing-info">Billed monthly</p>
                  )}
                </div>
                <div className="pricing-features">
                  <ul>
                    <li><i className="lni lni-checkmark-circle"></i> 20 resume analyses per day</li>
                    <li><i className="lni lni-checkmark-circle"></i> Higher daily usage than Basic</li>
                    <li><i className="lni lni-checkmark-circle"></i> Advanced AI matching</li>
                    <li><i className="lni lni-checkmark-circle"></i> Priority support</li>
                    <li><i className="lni lni-checkmark-circle"></i> Export results as PDF & DOCX</li>
                    <li><i className="lni lni-checkmark-circle"></i> Detailed analytics</li>
                  </ul>
                </div>
                <Link to="/login" className="pricing-btn">Upgrade</Link>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="col-lg-4 col-md-6">
              <div className="pricing-card pricing-card-featured glass-card wow fadeInUp" data-wow-delay=".2s">
                <div className="featured-badge">
                  <i className="lni lni-checkmark-circle"></i>
                </div>
                <div className="pricing-header">
                  <h3 className="plan-name gradient-text">Pro</h3>
                  <p className="plan-subtitle">Best for teams and enterprises</p>
                  <div className="price-wrapper">
                    <span className="currency">$</span>
                    <span className="price">{pricingPlan === 'annual' ? '41.67' : '50'}</span>
                    <span className="period">/ month</span>
                  </div>
                  {pricingPlan === 'annual' ? (
                    <>
                      <p className="billing-info">$500 billed annually</p>
                      <span className="save-badge save-badge-featured">SAVE 100%</span>
                    </>
                  ) : (
                    <p className="billing-info">Billed monthly</p>
                  )}
                </div>
                <div className="pricing-features">
                  <ul>
                    <li><i className="lni lni-checkmark-circle"></i> Unlimited resume analyses</li>
                    <li><i className="lni lni-checkmark-circle"></i> No daily limits</li>
                    <li><i className="lni lni-checkmark-circle"></i> Premium AI matching</li>
                    <li><i className="lni lni-checkmark-circle"></i> 24/7 priority support</li>
                    <li><i className="lni lni-checkmark-circle"></i> All export formats</li>
                    <li><i className="lni lni-checkmark-circle"></i> Advanced analytics & insights</li>
                    <li><i className="lni lni-checkmark-circle"></i> API access</li>
                    <li><i className="lni lni-checkmark-circle"></i> Custom integrations</li>
                  </ul>
                </div>
                <Link to="/login" className="pricing-btn pricing-btn-featured">Get Started</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Contact Info */}
      <section id="contact" className="ud-cta modern-section cta-header-bg">
        <div className="container">
          <div className="row align-items-center mb-5">
            <div className="col-lg-9">
              <div className="ud-cta-content-wrapper wow fadeInUp" data-wow-delay=".2s">
                <div className="ud-cta-title">
                  <h2 className="gradient-text">Ready to Transform Your Hiring Process?</h2>
                  <p className="light-text">Join hundreds of recruiters who are already saving time and hiring better with NovaMatch.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="ud-cta-btn-wrapper wow fadeInUp" data-wow-delay=".25s">
                <Link to="/register" className="ud-main-btn ud-gradient-btn">
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="row justify-content-center mt-4 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="col-lg-3 col-md-5 col-sm-6">
              <div className="ud-contact-content-wrapper text-center wow fadeInUp" data-wow-delay=".3s">
                <div className="ud-single-info">
                  <div className="ud-info-icon" style={{ fontSize: '30px', marginBottom: '10px' }}>
                    <i className="lni lni-map-marker"></i>
                  </div>
                  <div className="ud-info-meta">
                    <h5 style={{ color: 'rgba(255, 255, 255, 0.95)', marginBottom: '8px', fontSize: '16px' }}>Our Location</h5>
                    <p className="light-text" style={{ fontSize: '14px', marginBottom: '0' }}>1186 Satellite Blvd, Suite 250<br />Suwanee, GA 30024</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-5 col-sm-6">
              <div className="ud-contact-content-wrapper text-center wow fadeInUp" data-wow-delay=".35s">
                <div className="ud-single-info">
                  <div className="ud-info-icon" style={{ fontSize: '30px', marginBottom: '10px' }}>
                    <i className="lni lni-envelope"></i>
                  </div>
                  <div className="ud-info-meta">
                    <h5 style={{ color: 'rgba(255, 255, 255, 0.95)', marginBottom: '8px', fontSize: '16px' }}>Email Us</h5>
                    <p className="light-text" style={{ fontSize: '14px', marginBottom: '0' }}>novalink.its@novalink-solutions.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
